import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// ── Randomized identifiers (change every page load) ──
function rid() {
  return '_' + Math.random().toString(36).slice(2, 10);
}

const BAIT_CONFIGS = [
  { classes: ['ad-banner', 'ad-wrapper', 'adsbox', 'ad-placement'], attrs: {} },
  { classes: ['sponsored-content', 'ad-container', 'adunit'], attrs: { 'data-ad-slot': '1' } },
  { classes: ['google-ad', 'dfp-ad-unit', 'adsbygoogle'], attrs: { 'data-ad-client': 'ca-pub-0' } },
];

/**
 * Multi-signal adblock detection.
 *
 * Signal 1  – Bait element: ad-class div hidden/removed by cosmetic filters
 * Signal 2  – Second bait element: different ad-related attributes
 * Signal 3  – Script resource load: attempt to load a script from a known
 *             ad-network URL pattern — blocked by network-level filters
 * Signal 4  – Google AdSense check: if the AdSense script tag is in the page,
 *             verify the global `adsbygoogle` object actually initialised
 *
 * Decision:  If ANY signal fires → adblock detected.  Each signal is
 *            independent, so blocking one detection method still leaves others.
 */
export function useAdblockDetector() {
  const [adblockDetected, setAdblockDetected] = useState(false);
  const interceptorRef = useRef(null);
  const signalsRef = useRef({ bait1: false, bait2: false, scriptLoad: false, adsenseGlobal: false });
  const resolvedRef = useRef(false);

  function flag() {
    if (!resolvedRef.current) {
      resolvedRef.current = true;
      setAdblockDetected(true);
    }
  }

  // ── Signal 1 & 2: Two independent bait elements ──
  useEffect(() => {
    const baits = [];
    const cfg1 = BAIT_CONFIGS[0];
    const cfg2 = BAIT_CONFIGS[Math.floor(Math.random() * 2) + 1];

    [cfg1, cfg2].forEach((cfg, idx) => {
      const el = document.createElement('div');
      const id = rid();
      el.id = id;
      el.className = cfg.classes[Math.floor(Math.random() * cfg.classes.length)] + ' ' + rid();
      Object.entries(cfg.attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.style.cssText =
        'width:1px;height:1px;position:absolute;top:-9999px;left:-9999px;pointer-events:none;opacity:0.01;';
      el.innerHTML = '&nbsp;';
      document.body.appendChild(el);
      baits.push({ el, id, signalKey: idx === 0 ? 'bait1' : 'bait2' });
    });

    const timer = setTimeout(() => {
      baits.forEach(({ id, signalKey }) => {
        const el = document.getElementById(id);
        const blocked =
          !el ||
          el.offsetHeight === 0 ||
          el.offsetWidth === 0 ||
          window.getComputedStyle(el).display === 'none' ||
          window.getComputedStyle(el).visibility === 'hidden';
        if (blocked) {
          signalsRef.current[signalKey] = true;
          flag();
        }
        if (el) el.remove();
      });
    }, 600);

    return () => {
      clearTimeout(timer);
      baits.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  // ── Signal 3: Script resource load test ──
  // Attempt to load a tiny script from a URL that matches well-known ad
  // filter patterns.  Adblockers with network-level blocking will prevent
  // the <script> from loading, triggering the onerror callback.
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    // This URL matches EasyList / uBlock default filter patterns.
    // It will be blocked by virtually all adblockers.  If the resource
    // fails to load, that's a strong signal.
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
    script.async = true;

    script.onerror = () => {
      signalsRef.current.scriptLoad = true;
      flag();
      script.remove();
    };
    script.onload = () => {
      script.remove();
    };

    // Timeout fallback: if neither fires in 3s, treat as blocked
    const fallback = setTimeout(() => {
      if (!script.onload && !signalsRef.current.scriptLoad) {
        signalsRef.current.scriptLoad = true;
        flag();
      }
      script.remove();
    }, 3000);

    document.head.appendChild(script);
    return () => {
      clearTimeout(fallback);
      if (script.parentNode) script.remove();
    };
  }, []);

  // ── Signal 4: Google AdSense global check ──
  // If an actual AdSense script tag is present on the page, check whether the
  // global `adsbygoogle` object initialized. Skip this signal when the site is
  // intentionally not loading AdSense before approval.
  useEffect(() => {
    const hasAdSenseScript = Array.from(document.scripts).some((script) =>
      script.src.includes('pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=')
    );
    if (!hasAdSenseScript) return undefined;

    const timer = setTimeout(() => {
      // Check if window.adsbygoogle exists and is an array (loaded successfully)
      const adsenseLoaded =
        typeof window.adsbygoogle !== 'undefined' &&
        window.adsbygoogle !== null;
      if (!adsenseLoaded) {
        signalsRef.current.adsenseGlobal = true;
        flag();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // ── Axios interceptor ──
  useEffect(() => {
    if (adblockDetected && interceptorRef.current === null) {
      interceptorRef.current = axios.interceptors.request.use((config) => {
        config.headers = config.headers || {};
        config.headers['x-adblock-status'] = 'detected';
        return config;
      });
    }
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.request.eject(interceptorRef.current);
        interceptorRef.current = null;
      }
    };
  }, [adblockDetected]);

  return adblockDetected;
}


// ── Security ban detection ──
// The ban cookie is __Host-sc_sb (HttpOnly, set by the server).
// The frontend cannot read it directly — it detects bans from:
// 1. API 403 responses with security_banned: true
// 2. The ban cookie causes the SecurityBanMiddleware to reject all requests with 403

const SECURITY_OVERLAY_ID = 'sc-security-ban-overlay';

export function useSecurityBanDetector() {
  const [securityBanned, setSecurityBanned] = useState(false);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && error.response?.data?.security_banned) {
          setSecurityBanned(true);
          showSecurityBanScreen();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return securityBanned;
}

function showSecurityBanScreen() {
  if (document.getElementById(SECURITY_OVERLAY_ID)) return;

  const style = document.createElement('style');
  style.textContent = `
    #${SECURITY_OVERLAY_ID} {
      position: fixed !important;
      top: 0 !important; left: 0 !important;
      width: 100vw !important; height: 100vh !important;
      z-index: 2147483647 !important;
      background: #0a0a0a !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-direction: column !important;
      color: #e5e5e5 !important;
      font-family: 'Georgia', serif !important;
      text-align: center !important;
      padding: 2rem !important;
    }
    #${SECURITY_OVERLAY_ID} h2 {
      font-size: 2rem !important;
      color: #dc2626 !important;
      margin-bottom: 1rem !important;
    }
    #${SECURITY_OVERLAY_ID} p {
      font-size: 1.1rem !important;
      max-width: 520px !important;
      line-height: 1.7 !important;
      color: #737373 !important;
    }
    body.sc-security-banned { overflow: hidden !important; height: 100vh !important; }
  `;
  document.head.appendChild(style);

  const overlay = document.createElement('div');
  overlay.id = SECURITY_OVERLAY_ID;
  overlay.setAttribute('data-testid', 'security-ban-overlay');
  overlay.innerHTML = `
    <svg style="width:64px;height:64px;margin-bottom:1.5rem;opacity:0.5" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
    </svg>
    <h2>Access Revoked</h2>
    <p>Your access to Secrets Club has been permanently revoked due to security violations. This action was taken by a site administrator.</p>
    <p style="margin-top:1rem !important; font-size:0.85rem !important; color:#525252 !important;">If you believe this is an error, contact the site administrator.</p>
  `;
  document.body.appendChild(overlay);
  document.body.classList.add('sc-security-banned');
}
